import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import AdminLayout from "@/components/layout/AdminLayout";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Eye, Trash2, Send, Download, X, Plus as PlusIcon, CreditCard } from "lucide-react";
import { formatMoney, formatDate } from "@/lib/format";
import { Modal, FieldText } from "./Customers";
import { Select } from "./Services";

const STATUS_COLOR = {
  draft: "bg-white/[0.08] text-white",
  sent: "bg-blue-500/15 text-blue-300",
  paid: "bg-[color:var(--bd-green)]/20 text-[color:var(--bd-green-bright)]",
  overdue: "bg-[color:var(--bd-red)]/20 text-[color:var(--bd-red-bright)]",
  cancelled: "bg-white/[0.04] text-[color:var(--ivory-muted)]",
};

export default function Invoices() {
  const [list, setList] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [services, setServices] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const load = async () => {
    try {
      const [inv, cu, sv] = await Promise.all([
        api.get("/invoices"),
        api.get("/customers"),
        api.get("/services"),
      ]);
      setList(inv.data);
      setCustomers(cu.data);
      setServices(sv.data);
    } catch {
      toast.error("Failed to load invoices");
    }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (searchParams.get("new") === "1") {
      setShowCreate(true);
      searchParams.delete("new");
      setSearchParams(searchParams);
    }
  }, [searchParams, setSearchParams]);

  const del = async (inv) => {
    if (!confirm(`Delete invoice ${inv.invoice_number}?`)) return;
    try { await api.delete(`/invoices/${inv.id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Delete failed"); }
  };

  const send = async (inv) => {
    if (!confirm(`Email invoice ${inv.invoice_number} to ${inv.customer_snapshot?.email || "customer"}?`)) return;
    try {
      const { data } = await api.post(`/invoices/${inv.id}/send`);
      if (data.skipped) toast.message("Email service not configured", { description: "Set RESEND_API_KEY in backend .env to actually send. Invoice marked as 'sent'." });
      else if (data.ok) toast.success("Invoice sent");
      else toast.error(data.message || "Send failed");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Send failed");
    }
  };

  const downloadPdf = (inv) => {
    window.open(`${API}/invoices/${inv.id}/pdf`, "_blank");
  };

  return (
    <AdminLayout title="Invoices">
      <div className="flex items-center justify-between mb-6">
        <div className="text-sm text-[color:var(--ivory-muted)]">{list.length} invoice{list.length !== 1 ? "s" : ""}</div>
        <button onClick={() => setShowCreate(true)} className="pill pill-primary inline-flex items-center gap-2 text-sm" data-testid="add-invoice-button">
          <Plus className="w-4 h-4" /> New invoice
        </button>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-white/10 bg-white/[0.02]">
        <table className="w-full text-sm" data-testid="invoices-table">
          <thead className="bg-white/[0.03] text-[color:var(--ivory-muted)] text-xs uppercase tracking-[0.15em]">
            <tr>
              <th className="px-4 py-3 text-left">Number</th>
              <th className="px-4 py-3 text-left">Customer</th>
              <th className="px-4 py-3 text-left">Issue</th>
              <th className="px-4 py-3 text-left">Due</th>
              <th className="px-4 py-3 text-right">Total</th>
              <th className="px-4 py-3 text-right">Paid</th>
              <th className="px-4 py-3 text-left">Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {list.map((i) => (
              <tr key={i.id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`invoice-row-${i.id}`}>
                <td className="px-4 py-3 font-mono text-xs">{i.invoice_number}</td>
                <td className="px-4 py-3">{i.customer_snapshot?.name || "—"}</td>
                <td className="px-4 py-3">{formatDate(i.issue_date)}</td>
                <td className="px-4 py-3">{formatDate(i.due_date)}</td>
                <td className="px-4 py-3 text-right font-medium">{formatMoney(i.total, i.currency)}</td>
                <td className="px-4 py-3 text-right">{formatMoney(i.amount_paid || 0, i.currency)}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 rounded-full text-xs uppercase tracking-wide ${STATUS_COLOR[i.status] || STATUS_COLOR.draft}`}>{i.status}</span>
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button onClick={() => setViewing(i)} className="p-2 hover:text-white text-[color:var(--ivory-muted)]" title="View" data-testid={`view-invoice-${i.id}`}>
                    <Eye className="w-4 h-4" />
                  </button>
                  <button onClick={() => downloadPdf(i)} className="p-2 hover:text-white text-[color:var(--ivory-muted)]" title="PDF">
                    <Download className="w-4 h-4" />
                  </button>
                  <button onClick={() => send(i)} className="p-2 hover:text-[color:var(--bd-green-bright)] text-[color:var(--ivory-muted)]" title="Email">
                    <Send className="w-4 h-4" />
                  </button>
                  <button onClick={() => del(i)} className="p-2 hover:text-[color:var(--bd-red-bright)] text-[color:var(--ivory-muted)]" title="Delete">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
            {list.length === 0 && (
              <tr><td colSpan="8" className="p-12 text-center text-[color:var(--ivory-muted)]">No invoices yet. Click "New invoice" to create one.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {showCreate && (
        <InvoiceCreateModal
          customers={customers}
          services={services}
          onClose={() => setShowCreate(false)}
          onSaved={() => { setShowCreate(false); load(); }}
        />
      )}

      {viewing && (
        <InvoiceViewModal invoice={viewing} onClose={() => setViewing(null)} onChanged={load} />
      )}
    </AdminLayout>
  );
}

function InvoiceCreateModal({ customers, services, onClose, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [customer_id, setCustomerId] = useState("");
  const [currency, setCurrency] = useState("SEK");
  const [issue_date, setIssueDate] = useState(today);
  const [due_date, setDueDate] = useState(new Date(Date.now() + 14 * 86400000).toISOString().slice(0, 10));
  const [items, setItems] = useState([{ description: "", quantity: 1, unit_price: 0, vat_rate: 25 }]);
  const [discount, setDiscount] = useState(0);
  const [notes, setNotes] = useState("");
  const [status, setStatus] = useState("draft");
  const [submitting, setSubmitting] = useState(false);

  const updateItem = (i, k, v) => {
    setItems((p) => p.map((it, idx) => idx === i ? { ...it, [k]: v } : it));
  };
  const addItem = () => setItems((p) => [...p, { description: "", quantity: 1, unit_price: 0, vat_rate: 25 }]);
  const removeItem = (i) => setItems((p) => p.length === 1 ? p : p.filter((_, idx) => idx !== i));
  const applyService = (i, sid) => {
    const s = services.find((x) => x.id === sid);
    if (s) {
      setItems((p) => p.map((it, idx) => idx === i ? { ...it, description: s.name, unit_price: Number(s.price) || 0, vat_rate: Number(s.vat_rate) || 25, service_id: s.id } : it));
    }
  };

  const totals = (() => {
    let sub = 0, vat = 0;
    items.forEach((it) => {
      const net = Number(it.quantity || 0) * Number(it.unit_price || 0);
      sub += net;
      vat += net * (Number(it.vat_rate || 0) / 100);
    });
    const d = Number(discount || 0);
    const subAfter = Math.max(0, sub - d);
    const vatAfter = sub > 0 ? vat * (subAfter / sub) : 0;
    return { subtotal: subAfter, vat: vatAfter, total: subAfter + vatAfter };
  })();

  const save = async (e) => {
    e.preventDefault();
    if (!customer_id) { toast.error("Pick a customer"); return; }
    if (items.some((it) => !it.description.trim())) { toast.error("Every line needs a description"); return; }
    setSubmitting(true);
    try {
      const payload = {
        customer_id, currency,
        issue_date: new Date(issue_date).toISOString(),
        due_date: new Date(due_date).toISOString(),
        items: items.map((it) => ({
          description: it.description,
          quantity: Number(it.quantity || 1),
          unit_price: Number(it.unit_price || 0),
          vat_rate: Number(it.vat_rate || 0),
          service_id: it.service_id || null,
        })),
        discount: Number(discount || 0),
        notes: notes || null,
        status,
      };
      await api.post("/invoices", payload);
      toast.success("Invoice created");
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Create failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal title="New invoice" wide onClose={onClose}>
      <form onSubmit={save} className="space-y-5" data-testid="invoice-form">
        <div className="grid sm:grid-cols-4 gap-3">
          <div className="sm:col-span-2">
            <label className="text-xs uppercase tracking-[0.2em] text-[color:var(--ivory-muted)] mb-2 block">Customer *</label>
            <select required value={customer_id} onChange={(e) => setCustomerId(e.target.value)} className="email-input pl-4 appearance-none cursor-pointer" data-testid="invoice-customer">
              <option value="" style={{ background: "#0E1216" }}>Select customer…</option>
              {customers.map((c) => <option key={c.id} value={c.id} style={{ background: "#0E1216" }}>{c.name}</option>)}
            </select>
          </div>
          <Select label="Currency" value={currency} onChange={setCurrency} options={[{ value: "SEK", label: "SEK" }, { value: "BDT", label: "BDT" }]} />
          <Select label="Status" value={status} onChange={setStatus} options={[
            { value: "draft", label: "Draft" }, { value: "sent", label: "Sent" },
          ]} />
          <FieldText type="date" label="Issue date" value={issue_date} onChange={setIssueDate} />
          <FieldText type="date" label="Due date" value={due_date} onChange={setDueDate} />
        </div>

        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[color:var(--ivory-muted)] mb-2">Line items</div>
          <div className="space-y-2">
            {items.map((it, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`invoice-item-${i}`}>
                <div className="col-span-12 sm:col-span-5">
                  <input value={it.description} onChange={(e) => updateItem(i, "description", e.target.value)} placeholder="Description" className="email-input pl-4" />
                  {services.length > 0 && (
                    <select onChange={(e) => e.target.value && applyService(i, e.target.value)} value="" className="mt-1 text-xs bg-transparent text-[color:var(--ivory-muted)] outline-none cursor-pointer">
                      <option value="" style={{ background: "#0E1216" }}>← Use service template</option>
                      {services.map((s) => <option key={s.id} value={s.id} style={{ background: "#0E1216" }}>{s.name} — {formatMoney(s.price, s.currency)}</option>)}
                    </select>
                  )}
                </div>
                <div className="col-span-3 sm:col-span-2">
                  <input type="number" step="0.01" value={it.quantity} onChange={(e) => updateItem(i, "quantity", e.target.value)} placeholder="Qty" className="email-input pl-3" />
                </div>
                <div className="col-span-4 sm:col-span-2">
                  <input type="number" step="0.01" value={it.unit_price} onChange={(e) => updateItem(i, "unit_price", e.target.value)} placeholder="Unit price" className="email-input pl-3" />
                </div>
                <div className="col-span-3 sm:col-span-2">
                  <input type="number" step="0.01" value={it.vat_rate} onChange={(e) => updateItem(i, "vat_rate", e.target.value)} placeholder="VAT %" className="email-input pl-3" />
                </div>
                <div className="col-span-2 sm:col-span-1 text-right">
                  <button type="button" onClick={() => removeItem(i)} className="p-2 text-[color:var(--ivory-muted)] hover:text-[color:var(--bd-red-bright)]">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
            <button type="button" onClick={addItem} className="text-sm text-[color:var(--bd-green-bright)] inline-flex items-center gap-1 mt-2">
              <PlusIcon className="w-3.5 h-3.5" /> Add line
            </button>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <FieldText type="number" label="Discount (pre-VAT)" value={discount} onChange={setDiscount} />
            <div className="mt-3">
              <label className="text-xs uppercase tracking-[0.2em] text-[color:var(--ivory-muted)] mb-2 block">Notes</label>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className="email-input pl-4 py-3" style={{ borderRadius: 12, resize: "vertical" }} />
            </div>
          </div>
          <div className="p-5 rounded-2xl border border-white/10 bg-white/[0.02] space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-[color:var(--ivory-muted)]">Subtotal</span><span>{formatMoney(totals.subtotal, currency)}</span></div>
            <div className="flex justify-between"><span className="text-[color:var(--ivory-muted)]">VAT</span><span>{formatMoney(totals.vat, currency)}</span></div>
            <div className="flex justify-between text-lg font-display pt-2 border-t border-white/5"><span>Total</span><span>{formatMoney(totals.total, currency)}</span></div>
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className="pill border border-white/15 text-white">Cancel</button>
          <button type="submit" disabled={submitting} className="pill pill-primary disabled:opacity-50" data-testid="invoice-save">
            {submitting ? "Saving…" : "Create invoice"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function InvoiceViewModal({ invoice, onClose, onChanged }) {
  const [payments, setPayments] = useState([]);
  const [amount, setAmount] = useState((invoice.total - (invoice.amount_paid || 0)).toFixed(2));
  const [method, setMethod] = useState("manual");
  const [reference, setReference] = useState("");

  const loadPayments = async () => {
    try { const { data } = await api.get(`/invoices/${invoice.id}/payments`); setPayments(data); }
    catch {}
  };
  useEffect(() => { loadPayments(); }, [invoice.id]);

  const addPayment = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/invoices/${invoice.id}/payments`, {
        invoice_id: invoice.id,
        amount: Number(amount),
        method,
        reference: reference || null,
      });
      toast.success("Payment recorded");
      loadPayments();
      onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed");
    }
  };

  const balance = invoice.total - (invoice.amount_paid || 0);
  const payLink = `${window.location.origin}/pay/${invoice.public_token}`;

  return (
    <Modal title={`Invoice ${invoice.invoice_number}`} wide onClose={onClose}>
      <div className="space-y-6" data-testid="invoice-view">
        <div className="grid sm:grid-cols-2 gap-4 text-sm">
          <div className="p-4 rounded-xl border border-white/10 bg-white/[0.02]">
            <div className="text-xs uppercase tracking-[0.2em] text-[color:var(--ivory-muted)] mb-2">Customer</div>
            <div className="font-medium">{invoice.customer_snapshot?.name}</div>
            <div className="text-[color:var(--ivory-muted)]">{invoice.customer_snapshot?.email}</div>
          </div>
          <div className="p-4 rounded-xl border border-white/10 bg-white/[0.02]">
            <div className="text-xs uppercase tracking-[0.2em] text-[color:var(--ivory-muted)] mb-2">Dates</div>
            <div>Issue: {formatDate(invoice.issue_date)}</div>
            <div>Due: {formatDate(invoice.due_date)}</div>
            <div className="mt-1 capitalize">Status: <span className={`px-2 py-0.5 rounded-full text-xs ${STATUS_COLOR[invoice.status] || STATUS_COLOR.draft}`}>{invoice.status}</span></div>
          </div>
        </div>

        <div className="overflow-x-auto rounded-xl border border-white/10">
          <table className="w-full text-sm">
            <thead className="bg-white/[0.03] text-xs uppercase tracking-[0.15em] text-[color:var(--ivory-muted)]">
              <tr><th className="px-3 py-2 text-left">Item</th><th className="px-3 py-2">Qty</th><th className="px-3 py-2">Unit</th><th className="px-3 py-2">VAT</th><th className="px-3 py-2 text-right">Total</th></tr>
            </thead>
            <tbody>
              {invoice.items.map((it, idx) => (
                <tr key={idx} className="border-t border-white/5">
                  <td className="px-3 py-2">{it.description}</td>
                  <td className="px-3 py-2 text-center">{it.quantity}</td>
                  <td className="px-3 py-2 text-center">{formatMoney(it.unit_price, invoice.currency)}</td>
                  <td className="px-3 py-2 text-center">{it.vat_rate}%</td>
                  <td className="px-3 py-2 text-right">{formatMoney(it.quantity * it.unit_price, invoice.currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div className="p-4 rounded-xl border border-white/10 bg-white/[0.02] text-sm space-y-1.5">
            <div className="flex justify-between"><span className="text-[color:var(--ivory-muted)]">Subtotal</span><span>{formatMoney(invoice.subtotal, invoice.currency)}</span></div>
            <div className="flex justify-between"><span className="text-[color:var(--ivory-muted)]">Discount</span><span>−{formatMoney(invoice.discount, invoice.currency)}</span></div>
            <div className="flex justify-between"><span className="text-[color:var(--ivory-muted)]">VAT</span><span>{formatMoney(invoice.vat_amount, invoice.currency)}</span></div>
            <div className="flex justify-between font-display text-lg pt-2 border-t border-white/5"><span>Total</span><span>{formatMoney(invoice.total, invoice.currency)}</span></div>
            <div className="flex justify-between text-[color:var(--bd-green-bright)]"><span>Paid</span><span>{formatMoney(invoice.amount_paid || 0, invoice.currency)}</span></div>
            <div className="flex justify-between font-semibold text-[color:var(--bd-red-bright)]"><span>Balance</span><span>{formatMoney(balance, invoice.currency)}</span></div>
          </div>
          <div className="p-4 rounded-xl border border-white/10 bg-white/[0.02] text-sm">
            <div className="text-xs uppercase tracking-[0.2em] text-[color:var(--ivory-muted)] mb-2">Public pay link</div>
            <div className="break-all text-xs font-mono bg-black/40 p-2 rounded">{payLink}</div>
            <button onClick={() => { navigator.clipboard.writeText(payLink); toast.success("Copied"); }} className="mt-3 pill border border-white/15 text-white text-xs">Copy link</button>
            <a href={payLink} target="_blank" rel="noreferrer" className="ml-2 pill pill-primary text-xs inline-flex items-center gap-1.5">
              <CreditCard className="w-3.5 h-3.5" /> Open
            </a>
          </div>
        </div>

        {/* Add payment */}
        {balance > 0.01 && invoice.status !== "cancelled" && (
          <form onSubmit={addPayment} className="p-4 rounded-xl border border-white/10 bg-white/[0.02]" data-testid="add-payment-form">
            <div className="text-xs uppercase tracking-[0.2em] text-[color:var(--ivory-muted)] mb-3">Record payment</div>
            <div className="grid sm:grid-cols-4 gap-2">
              <FieldText type="number" label="Amount" value={amount} onChange={setAmount} />
              <Select label="Method" value={method} onChange={setMethod} options={[
                { value: "manual", label: "Manual" }, { value: "bank_transfer", label: "Bank transfer" }, { value: "stripe", label: "Stripe (manual)" },
              ]} />
              <FieldText label="Reference" value={reference} onChange={setReference} />
              <div className="flex items-end">
                <button type="submit" className="pill pill-primary w-full" data-testid="payment-submit">Add</button>
              </div>
            </div>
          </form>
        )}

        {/* Payments history */}
        {payments.length > 0 && (
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[color:var(--ivory-muted)] mb-2">Payments</div>
            <div className="rounded-xl border border-white/10 overflow-hidden">
              <table className="w-full text-sm">
                <tbody>
                  {payments.map((p) => (
                    <tr key={p.id} className="border-t border-white/5 first:border-t-0">
                      <td className="px-3 py-2">{formatDate(p.paid_at)}</td>
                      <td className="px-3 py-2 capitalize">{p.method}</td>
                      <td className="px-3 py-2">{p.reference || "—"}</td>
                      <td className="px-3 py-2 text-right font-medium">{formatMoney(p.amount, p.currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
